/**
 * BookingPage Component
 *
 * Complete booking flow for property reservations.
 */

import React, { useState } from 'react';
import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { PublicLayout, Container } from '../../components/layout';
import { functions } from '../../services/firebase';
import { useAuth } from '../../contexts';
import { usePricing, useCustomPricing, useCreateBooking } from './hooks';
import type { BookingFormData, GuestCount } from './hooks';
import { useListing } from '../Listing/hooks/useListing';
import { useAvailability } from '../Listing/hooks/useAvailability';
import {
  DateSelection,
  GuestDetails,
  PaymentSelection,
  CancellationPolicy,
  BookingSummary,
} from './components';

// Helper to parse date from URL
const parseDateParam = (param: string | null): Date | null => {
  if (!param) return null;
  const date = new Date(param + 'T12:00:00');
  return isNaN(date.getTime()) ? null : date;
};

// Add days helper
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const BookingPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const { listing: rawListing, isLoading: listingLoading } = useListing(listingId!);
  const { blockedDateSet } = useAvailability(listingId!);
  const { createBooking, isLoading: bookingLoading, error: bookingError } = useCreateBooking();

  // Map the real listing data to the shape BookingPage needs
  const listing = rawListing
    ? {
        id: rawListing.id,
        hostId: rawListing.host.id,
        title: rawListing.title,
        propertyType: rawListing.propertyType,
        photo: rawListing.photos[0]?.url || '',
        location: {
          city: rawListing.location.city,
          region: rawListing.location.area,
        },
        pricing: {
          nightlyRate: rawListing.pricing.nightlyRate,
          weekendRate: rawListing.pricing.weekendRate,
          cleaningFee: rawListing.pricing.cleaningFee,
          securityDeposit: 0,
        },
        maxGuests: rawListing.maxGuests,
        minimumStay: rawListing.minimumStay,
        instantBooking: rawListing.instantBooking,
        cancellationPolicy: rawListing.cancellationPolicy as 'flexible' | 'moderate' | 'strict',
        rating: rawListing.rating,
        reviewCount: rawListing.reviewCount,
      }
    : null;

  // Initialize form from URL params
  const [formData, setFormData] = useState<BookingFormData>(() => {
    const checkIn = parseDateParam(searchParams.get('checkIn')) || addDays(new Date(), 1);
    const checkOut = parseDateParam(searchParams.get('checkOut')) || addDays(new Date(), 3);

    return {
      listingId: listingId!,
      checkIn,
      checkOut,
      guests: {
        adults: parseInt(searchParams.get('adults') || searchParams.get('guests') || '2'),
        children: parseInt(searchParams.get('children') || '0'),
        infants: parseInt(searchParams.get('infants') || '0'),
      },
      paymentMethod: 'stripe',
      acknowledgedPolicy: false,
      specialRequests: '',
    };
  });

  const [submitError, setSubmitError] = useState<string | null>(null);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number } | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);

  // Calculate pricing (custom per-date overrides included)
  const customPrices = useCustomPricing(listingId, formData.checkIn, formData.checkOut);
  const pricing = usePricing(listing?.pricing, formData.checkIn, formData.checkOut, customPrices);

  const applyPromo = async () => {
    if (!pricing || !promoInput.trim()) return;
    setPromoChecking(true);
    try {
      const fn = httpsCallable<
        { code: string; subtotal: number; nights: number; commissionPercent: number },
        { valid: boolean; code?: string; discount: number; message: string }
      >(functions, 'validatePromoCode');
      const res = await fn({
        code: promoInput.trim(),
        subtotal: pricing.nightlyTotal + pricing.cleaningFee,
        nights: pricing.nights,
        commissionPercent: pricing.commissionPercent,
      });
      if (res.data.valid) {
        setAppliedPromo({ code: res.data.code || promoInput.trim().toUpperCase(), discount: res.data.discount });
        toast.success(res.data.message);
      } else {
        setAppliedPromo(null);
        toast.error(res.data.message || 'Invalid code');
      }
    } catch {
      toast.error('Could not check that code');
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput('');
  };

  // Redirect to login if not authenticated
  if (!authLoading && !isAuthenticated) {
    const returnUrl = `/booking/${listingId}?${searchParams.toString()}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} state={{ from: { pathname: returnUrl } }} replace />;
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!listing || !pricing) return;

    setSubmitError(null);

    try {
      const result = await createBooking(
        { ...formData, promoCode: appliedPromo?.code },
        {
          hostId: listing.hostId,
          instantBooking: listing.instantBooking,
          pricing: listing.pricing,
          title: listing.title,
          photo: listing.photo,
          location: `${listing.location.city}, ${listing.location.region}`,
        },
        {
          nights: pricing.nights,
          nightlyTotal: pricing.nightlyTotal,
          cleaningFee: pricing.cleaningFee,
          platformCommission: pricing.platformCommission,
          securityDeposit: pricing.securityDeposit,
          totalAmount: pricing.totalAmount,
          hostPayout: pricing.hostPayout,
        }
      );

      navigate(`/booking/confirmation/${result.id}`);
    } catch (err) {
      if (err instanceof Error && err.message === 'DATES_UNAVAILABLE') {
        setSubmitError('Sorry, these dates are no longer available. Please select different dates.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    }
  };

  // Update handlers
  const handleDatesChange = (checkIn: Date, checkOut: Date) => {
    setFormData((prev) => ({ ...prev, checkIn, checkOut }));
  };

  const handleGuestsChange = (guests: GuestCount) => {
    setFormData((prev) => ({ ...prev, guests }));
  };

  // Loading state
  if (listingLoading || authLoading) {
    return (
      <PublicLayout>
        <Container className="py-8">
          <BookingPageSkeleton />
        </Container>
      </PublicLayout>
    );
  }

  if (!listing) {
    return (
      <PublicLayout>
        <Container className="py-8">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Listing not found</h1>
            <p className="text-gray-600 mb-4">This listing may no longer be available.</p>
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 hover:underline"
            >
              Go back
            </button>
          </div>
        </Container>
      </PublicLayout>
    );
  }

  const canSubmit = formData.acknowledgedPolicy && pricing && !bookingLoading;

  return (
    <PublicLayout>
      <Container className="py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ChevronLeftIcon className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-12">
          {/* Left: Booking Form */}
          <div className="lg:col-span-3 space-y-6 lg:space-y-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Complete your booking
            </h1>

            {/* Error Message */}
            {(submitError || bookingError) && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                {submitError || bookingError}
              </div>
            )}

            {/* Trip Details Section */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Your trip</h2>
              <DateSelection
                checkIn={formData.checkIn}
                checkOut={formData.checkOut}
                onChange={handleDatesChange}
                blockedDates={blockedDateSet}
                minimumStay={listing.minimumStay}
              />
              <GuestDetails
                guests={formData.guests}
                maxGuests={listing.maxGuests}
                onChange={handleGuestsChange}
              />
            </section>

            {/* Message to Host */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Message to host</h2>
              <textarea
                value={formData.specialRequests || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, specialRequests: e.target.value }))
                }
                placeholder="Tell the host about your trip, any special requests, or questions..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </section>

            {/* Payment Method */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Payment method</h2>
              <PaymentSelection
                value={formData.paymentMethod}
                onChange={(method) =>
                  setFormData((prev) => ({ ...prev, paymentMethod: method }))
                }
              />
            </section>

            {/* Cancellation Policy */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Cancellation policy</h2>
              <CancellationPolicy
                policy={listing.cancellationPolicy}
                checkIn={formData.checkIn}
              />

              {/* Policy Acknowledgement */}
              <label className="flex items-start gap-3 mt-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.acknowledgedPolicy}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      acknowledgedPolicy: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  I understand and agree to the cancellation policy, house rules, and{' '}
                  <a href="/terms" className="text-primary-600 hover:underline">
                    Terms of Service
                  </a>
                  .
                </span>
              </label>
            </section>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full py-4 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {bookingLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner />
                  Processing...
                </span>
              ) : listing.instantBooking ? (
                'Confirm and Pay'
              ) : (
                'Request to Book'
              )}
            </button>

            {/* Instant vs Request info */}
            <p className="text-sm text-gray-500 text-center">
              {listing.instantBooking
                ? "You won't be charged yet. Payment details will be sent after confirmation."
                : "You'll only be charged if the host accepts your request within 24 hours."}
            </p>
          </div>

          {/* Right: Booking Summary (Sticky) */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <BookingSummary
                listing={listing}
                checkIn={formData.checkIn}
                checkOut={formData.checkOut}
                pricing={pricing}
              />

              {/* Promo code */}
              {pricing && (
                <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-4">
                  {appliedPromo ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-700">
                          Code {appliedPromo.code} applied
                        </p>
                        <p className="text-sm text-gray-900">
                          −${appliedPromo.discount.toFixed(2)} · New total $
                          {(pricing.totalAmount - appliedPromo.discount).toFixed(2)}
                        </p>
                      </div>
                      <button
                        onClick={removePromo}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Promo code
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                          onClick={applyPromo}
                          disabled={promoChecking || !promoInput.trim()}
                          className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50"
                        >
                          {promoChecking ? '...' : 'Apply'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

// Loading Skeleton
const BookingPageSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-48 mb-8" />
    <div className="grid lg:grid-cols-5 gap-6 lg:gap-12">
      <div className="lg:col-span-3 space-y-8">
        <div className="h-10 bg-gray-200 rounded w-64" />
        <div className="h-32 bg-gray-200 rounded-xl" />
        <div className="h-32 bg-gray-200 rounded-xl" />
        <div className="h-48 bg-gray-200 rounded-xl" />
      </div>
      <div className="lg:col-span-2">
        <div className="h-96 bg-gray-200 rounded-2xl" />
      </div>
    </div>
  </div>
);

// Icons
const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const LoadingSpinner: React.FC = () => (
  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default BookingPage;
